import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, PieChart, Pie, Legend,
} from "recharts"
import { getPerfilDetalle } from "./api"
import {
  formatCurrency, formatNumber, formatPercent, formatGrowth, formatFecha, formatFechaLarga,
  segmentoConfig, sugerenciaConfig, prioridadConfig, alertaConfig, scoreColor,
  colorPara,
} from "./helpers"

const TABS = [
  { id: "resumen",         label: "Resumen",          icon: "mdi-view-dashboard-outline" },
  { id: "tendencia",       label: "Tendencia",        icon: "mdi-chart-line" },
  { id: "comportamiento",  label: "Comportamiento",   icon: "mdi-calendar-clock" },
  { id: "productos",       label: "Productos",        icon: "mdi-package-variant" },
  { id: "sucursales",      label: "Sucursales/Pagos", icon: "mdi-source-branch" },
  { id: "predicciones",    label: "Predicciones",     icon: "mdi-crystal-ball" },
  { id: "sugerencias",     label: "Sugerencias",      icon: "mdi-lightbulb-on-outline" },
]

// ───────── Helpers UI ─────────

const StatPill = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 180px", minWidth: 0,
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: "12px 14px",
  }}>
    <div style={{
      fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600,
      letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 6,
    }}>
      <i className={`mdi ${icon}`} style={{ color: accent }} />
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
  </div>
)

StatPill.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const RfmBar = ({ label, score, max = 5, hint }) => {
  const pct = (Number(score) / max) * 100
  const color = scoreColor(score)
  return (
    <div style={{ flex: "1 1 180px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{score ?? "—"}/{max}</span>
      </div>
      <div style={{ height: 8, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width .3s" }} />
      </div>
      {hint && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>{hint}</div>}
    </div>
  )
}

RfmBar.propTypes = { label: PropTypes.string, score: PropTypes.number, max: PropTypes.number, hint: PropTypes.string }

const Insight = ({ children }) => (
  <span style={{
    display: "inline-block", padding: "3px 10px",
    background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af",
    borderRadius: 999, fontSize: 12, fontWeight: 500,
    marginRight: 6, marginBottom: 4,
  }}>{children}</span>
)
Insight.propTypes = { children: PropTypes.node }

// ───────── Sub-tabs ─────────

const ResumenSub = ({ perfil, clienteBase }) => {
  const k = perfil.kpis || {}
  const rfm = perfil.rfm || {}
  const cfgSeg = segmentoConfig(rfm.segmento)
  // Insights vienen del catálogo (no del drilldown)
  const insights = clienteBase?.insights || perfil.insights || []
  const alertas = perfil.alertas || []

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Banner del segmento + recomendación */}
      <div style={{
        background: `linear-gradient(135deg, ${cfgSeg.bg}, #ffffff)`,
        border: `1px solid ${cfgSeg.color}40`,
        borderLeft: `4px solid ${cfgSeg.color}`,
        borderRadius: 12, padding: "16px 20px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 28 }}>{cfgSeg.emoji}</span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, color: cfgSeg.color, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
              Segmento RFM
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
              {cfgSeg.label}
              {rfm.score_combinado && (
                <span style={{
                  fontSize: 12, fontWeight: 700, marginLeft: 10,
                  padding: "2px 8px", borderRadius: 6,
                  background: cfgSeg.color, color: "#fff",
                }}>
                  {rfm.score_combinado}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
              {rfm.recomendacion || cfgSeg.desc}
            </div>
          </div>
        </div>

        {/* Scores R/F/M */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 16 }}>
          <RfmBar label="Recency" score={rfm.score_r}
            hint={rfm.recency_dias != null ? `${rfm.recency_dias}d sin comprar` : ""} />
          <RfmBar label="Frequency" score={rfm.score_f}
            hint={rfm.frequency_facturas != null ? `${formatNumber(rfm.frequency_facturas)} facturas` : ""} />
          <RfmBar label="Monetary" score={rfm.score_m}
            hint={rfm.monetary_importe != null ? formatCurrency(rfm.monetary_importe) : ""} />
        </div>
      </div>

      {/* Alertas (si hay) */}
      {alertas.length > 0 && (
        <div style={{
          background: "#fef3c7", border: "1px solid #fde68a",
          borderRadius: 10, padding: "10px 14px",
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
            <i className="mdi mdi-alert-octagon-outline me-1" />
            Alertas activas
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {alertas.map((a, i) => {
              const cfg = alertaConfig(a.tipo || a)
              return (
                <span key={i} style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "4px 10px", borderRadius: 999,
                  background: cfg.bg, color: cfg.color,
                  fontSize: 12, fontWeight: 600,
                }}>
                  <i className={`mdi mdi-${cfg.icon}`} />
                  {cfg.label}
                  {typeof a === "object" && (a.mensaje || a.descripcion) && (
                    <span style={{ opacity: 0.85 }}>· {a.mensaje || a.descripcion}</span>
                  )}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* KPIs principales */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <StatPill icon="mdi-receipt-text-outline" label="Facturas"
          value={formatNumber(k.total_facturas ?? k.facturas)}
          accent="#3b82f6"
          sub={k.frecuencia_dias != null ? `cada ${formatNumber(k.frecuencia_dias, 0)} días` : ""} />
        <StatPill icon="mdi-cash-multiple" label="Ingreso neto"
          value={formatCurrency(k.ingreso_neto)}
          accent="#10b981"
          sub={k.ingreso_bruto != null ? `bruto ${formatCurrency(k.ingreso_bruto)}` : ""} />
        <StatPill icon="mdi-cash" label="Ticket promedio"
          value={formatCurrency(k.ticket_promedio)}
          accent="#8b5cf6"
          sub={k.ticket_mediano != null ? `mediana ${formatCurrency(k.ticket_mediano)}` : ""} />
        <StatPill icon="mdi-cash-100" label="Ticket máximo"
          value={formatCurrency(k.ticket_maximo)}
          accent="#f59e0b"
          sub={k.ticket_minimo != null ? `mín ${formatCurrency(k.ticket_minimo)}` : ""} />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <StatPill icon="mdi-undo-variant" label="Devoluciones"
          value={formatPercent(k.tasa_devolucion_pct ?? 0)}
          accent={(k.tasa_devolucion_pct || 0) > 5 ? "#dc2626" : "#475569"}
          sub={k.devoluciones != null ? `${formatNumber(k.devoluciones)} eventos` : ""} />
        <StatPill icon="mdi-account-clock-outline" label="Antigüedad"
          value={k.dias_como_cliente != null ? `${formatNumber(k.dias_como_cliente)} d` : "—"}
          accent="#06b6d4"
          sub={k.primera_compra ? `desde ${formatFecha(k.primera_compra)}` : ""} />
        <StatPill icon="mdi-calendar-check-outline" label="Última compra"
          value={formatFecha(k.ultima_compra)}
          accent="#ec4899"
          sub={k.dias_sin_comprar != null ? `hace ${formatNumber(k.dias_sin_comprar)}d` : ""} />
        <StatPill icon="mdi-star-circle-outline" label="Estado"
          value={k.es_activo ? "Activo" : "Inactivo"}
          accent={k.es_activo ? "#16a34a" : "#94a3b8"}
          sub={k.es_recurrente ? "Recurrente ✓" : "No recurrente"} />
      </div>

      {/* Insights del catálogo */}
      {insights.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 18px" }}>
          <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4, marginBottom: 10 }}>
            <i className="mdi mdi-tag-text-outline me-1" />
            Insights
          </div>
          <div>
            {insights.map((ins, i) => <Insight key={i}>{ins}</Insight>)}
          </div>
        </div>
      )}
    </div>
  )
}

ResumenSub.propTypes = { perfil: PropTypes.object, clienteBase: PropTypes.object }

const TendenciaSub = ({ perfil }) => {
  const t = perfil.tendencia || {}
  const series = (t.series_diarias || []).map(p => ({
    fecha: p.fecha,
    importe: Number(p.importe || 0),
    facturas: Number(p.facturas || 0),
  }))
  const comp = perfil.comparativo || {}
  const vsAnt = comp.vs_periodo_anterior || {}
  const vsPro = comp.vs_cliente_promedio || {}
  const vsTop = comp.vs_cliente_top_10 || {}
  const growth = formatGrowth(vsAnt.importe_pct ?? 0)
  const compFacturas = formatGrowth(vsAnt.facturas_pct ?? 0)
  const compTicket = formatGrowth(vsAnt.ticket_pct ?? 0)

  // Direccion → estilos
  const dir = String(t.direccion || "").toLowerCase()
  const dirCfg =
    dir === "creciente"   ? { bg: "#d1fae5", color: "#065f46", label: "Creciente" } :
    dir === "decreciente" ? { bg: "#fee2e2", color: "#991b1b", label: "Decreciente" } :
    dir === "estable"     ? { bg: "#dbeafe", color: "#1e40af", label: "Estable" } :
                            { bg: "#f1f5f9", color: "#475569", label: dir || "—" }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* KPIs comparativo */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <StatPill
          icon="mdi-trending-up" label="Ingreso vs anterior"
          value={<span style={{ color: growth.color }}>{growth.text}</span>}
          accent={growth.color}
        />
        <StatPill
          icon="mdi-receipt-text-arrow-right-outline" label="Facturas vs anterior"
          value={<span style={{ color: compFacturas.color }}>{compFacturas.text}</span>}
          accent={compFacturas.color}
        />
        <StatPill
          icon="mdi-cash" label="Ticket vs anterior"
          value={<span style={{ color: compTicket.color }}>{compTicket.text}</span>}
          accent={compTicket.color}
        />
      </div>

      {/* Comparativos vs promedio / top 10 */}
      {(vsPro.ticket_benchmark || vsPro.frecuencia_benchmark || vsTop.ticket_benchmark) ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
          {(vsPro.ticket_benchmark || vsPro.frecuencia_benchmark) && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                <i className="mdi mdi-account-tie-outline me-1" /> vs cliente promedio
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#475569" }}>Ticket</span>
                <strong style={{ color: formatGrowth(vsPro.ticket_delta_pct).color }}>
                  {formatGrowth(vsPro.ticket_delta_pct).text}
                </strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 4 }}>
                <span style={{ color: "#475569" }}>Frecuencia</span>
                <strong style={{ color: formatGrowth(vsPro.frecuencia_delta_pct).color }}>
                  {formatGrowth(vsPro.frecuencia_delta_pct).text}
                </strong>
              </div>
            </div>
          )}
          {vsTop.ticket_benchmark > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                <i className="mdi mdi-trophy-outline me-1" /> vs top 10%
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#475569" }}>Ticket</span>
                <strong style={{ color: formatGrowth(vsTop.ticket_delta_pct).color }}>
                  {formatGrowth(vsTop.ticket_delta_pct).text}
                </strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 4 }}>
                <span style={{ color: "#475569" }}>Frecuencia</span>
                <strong style={{ color: formatGrowth(vsTop.frecuencia_delta_pct).color }}>
                  {formatGrowth(vsTop.frecuencia_delta_pct).text}
                </strong>
              </div>
            </div>
          )}
          {(comp.percentil_monetary > 0 || comp.ranking_monetary > 0) && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                <i className="mdi mdi-podium me-1" /> Ranking del universo
              </div>
              {comp.ranking_monetary > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "#475569" }}>Posición</span>
                  <strong>#{formatNumber(comp.ranking_monetary)} de {formatNumber(comp.total_clientes_universo)}</strong>
                </div>
              )}
              {comp.percentil_monetary > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 4 }}>
                  <span style={{ color: "#475569" }}>Percentil</span>
                  <strong style={{ color: "#10b981" }}>P{comp.percentil_monetary}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}

      {/* Línea de tendencia */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
        <div className="d-flex justify-content-between align-items-baseline mb-2 flex-wrap gap-2">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-chart-line me-2 text-primary" />
            Serie diaria de ingreso
          </h6>
          {t.direccion && (
            <span style={{
              padding: "2px 10px", borderRadius: 999,
              background: dirCfg.bg, color: dirCfg.color,
              fontSize: 11, fontWeight: 700,
            }}>
              {dirCfg.label}
              {t.pendiente_diaria_pct != null && ` · pendiente ${formatPercent(t.pendiente_diaria_pct, 2)}`}
            </span>
          )}
        </div>
        {series.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            <i className="mdi mdi-chart-line-stacked" style={{ fontSize: 36 }} />
            <div className="mt-2">Sin serie diaria.</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} tickFormatter={formatFecha} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
              <Tooltip
                formatter={(v, name) => name === "importe" ? formatCurrency(v) : formatNumber(v)}
                labelFormatter={l => formatFecha(l)}
              />
              <Line type="monotone" dataKey="importe" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

TendenciaSub.propTypes = { perfil: PropTypes.object }

const ComportamientoSub = ({ perfil }) => {
  const ct = perfil.comportamiento_temporal || {}
  // por_dia_semana viene como [{dia, numero_dia, facturas, importe}]
  const dias = (ct.por_dia_semana || []).map(d => ({
    dia: d.dia,
    facturas: Number(d.facturas || 0),
    importe: Number(d.importe || 0),
  }))
  const intervalos = ct.intervalos_compras || []

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <StatPill icon="mdi-calendar-star-outline" label="Día preferido"
          value={ct.dia_semana_preferido || "—"} accent="#3b82f6" />
        <StatPill icon="mdi-clock-time-four-outline" label="Hora pico"
          value={ct.hora_pico ? `${ct.hora_pico}:00` : "—"} accent="#06b6d4" />
        <StatPill icon="mdi-timer-sand" label="Intervalo promedio"
          value={ct.intervalo_promedio_dias != null ? `${formatNumber(ct.intervalo_promedio_dias, 0)} d` : "—"}
          accent="#8b5cf6"
          sub={ct.intervalo_variacion_pct != null ? `variación ${formatPercent(ct.intervalo_variacion_pct, 0)}` : ""} />
        <StatPill icon="mdi-pulse" label="Regularidad"
          value={ct.regularidad || "—"} accent="#f59e0b" />
      </div>

      {/* Día semana — gráfico de facturas */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
          <i className="mdi mdi-calendar-week me-2 text-primary" />
          Compras por día de semana
        </h6>
        {dias.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>Sin datos.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dias}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v, name) => name === "importe" ? formatCurrency(v) : `${formatNumber(v)} facturas`}
              />
              <Bar dataKey="facturas" fill="#3b82f6" radius={[6, 6, 0, 0]}>
                {dias.map((d, i) => (
                  <Cell key={i} fill={d.dia === ct.dia_semana_preferido ? "#10b981" : "#3b82f6"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Día semana — importe */}
      {dias.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
            <i className="mdi mdi-cash-multiple me-2 text-success" />
            Importe por día de semana
          </h6>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dias}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
              <Tooltip formatter={v => formatCurrency(v)} />
              <Bar dataKey="importe" fill="#10b981" radius={[6, 6, 0, 0]}>
                {dias.map((d, i) => (
                  <Cell key={i} fill={d.dia === ct.dia_semana_preferido ? "#065f46" : "#10b981"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Intervalos recientes */}
      {intervalos.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
            <i className="mdi mdi-timer-outline me-2 text-primary" />
            Últimos intervalos (días entre compras)
          </h6>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {intervalos.map((d, i) => (
              <span key={i} style={{
                padding: "6px 12px", borderRadius: 8,
                background: "#eff6ff", border: "1px solid #bfdbfe",
                color: "#1e40af", fontWeight: 700, fontSize: 14,
              }}>
                {d}d
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

ComportamientoSub.propTypes = { perfil: PropTypes.object }

const ProductosSub = ({ perfil }) => {
  const productos = perfil.productos_preferidos || []

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
          <i className="mdi mdi-package-variant me-2 text-primary" />
          Top productos preferidos
        </h6>
        {productos.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
            <i className="mdi mdi-package-variant-closed-remove" style={{ fontSize: 32 }} />
            <div className="mt-2">Sin productos para mostrar.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Producto</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Veces</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Cantidad</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Importe</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Última</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Lift</th>
                </tr>
              </thead>
              <tbody>
                {productos.map((p, i) => {
                  const esFav = !!p.es_favorito
                  return (
                    <tr key={i} style={{ background: esFav ? "#fffbeb" : (i % 2 === 0 ? "#fff" : "#fafbfc") }}>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                        {esFav && <span style={{ marginRight: 4 }}>⭐</span>}
                        <span style={{ fontWeight: esFav ? 700 : 500 }}>{p.descripcion || p.id_producto}</span>
                        {p.id_producto && <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 6 }}>· {p.id_producto}</span>}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9", fontWeight: 600 }}>
                        {formatNumber(p.veces_comprado)}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9" }}>
                        {formatNumber(p.cantidad_total, 2)}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9", fontWeight: 600, color: "#10b981" }}>
                        {formatCurrency(p.importe_total)}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9", fontSize: 11, color: "#94a3b8" }}>
                        {formatFecha(p.ultima_compra)}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9", fontSize: 11 }}>
                        {p.lift_vs_promedio > 0 ? (
                          <span style={{ color: "#92400e", fontWeight: 700 }}>×{Number(p.lift_vs_promedio).toFixed(2)}</span>
                        ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

ProductosSub.propTypes = { perfil: PropTypes.object }

const SucursalesPagosSub = ({ perfil }) => {
  const sucursales = (perfil.sucursales_visitadas || []).map((s, i) => ({
    nombre: s.sucursal || `Suc. ${i + 1}`,
    facturas: Number(s.facturas || 0),
    importe: Number(s.importe || 0),
    participacion_pct: Number(s.participacion_pct || 0),
    ultima_visita: s.ultima_visita,
  }))
  const pagos = (perfil.metodos_pago_preferidos || []).map((m, i) => ({
    nombre: m.metodo || m.medio || m.nombre || `Otro ${i + 1}`,
    importe: Number(m.importe || 0),
    facturas: Number(m.facturas || 0),
  }))

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
          <i className="mdi mdi-source-branch me-2 text-primary" />
          Sucursales visitadas
        </h6>
        {sucursales.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>Sin datos.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={Math.max(200, sucursales.length * 36 + 60)}>
              <BarChart data={sucursales} layout="vertical" margin={{ left: 30, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
                <YAxis type="category" dataKey="nombre" tick={{ fontSize: 11 }} width={140} />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Bar dataKey="importe" fill="#3b82f6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ marginTop: 8, fontSize: 11, color: "#94a3b8" }}>
              {sucursales.map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                  <span>{s.nombre} · {formatNumber(s.facturas)} facturas</span>
                  <strong style={{ color: "#475569" }}>{formatPercent(s.participacion_pct, 1)}</strong>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
          <i className="mdi mdi-credit-card-outline me-2 text-primary" />
          Métodos de pago
        </h6>
        {pagos.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            <i className="mdi mdi-credit-card-off-outline" style={{ fontSize: 32 }} />
            <div className="mt-2">Sin métodos de pago en el período.</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={pagos}
                dataKey="importe"
                nameKey="nombre"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
              >
                {pagos.map((p, i) => <Cell key={i} fill={colorPara(i)} />)}
              </Pie>
              <Tooltip formatter={v => formatCurrency(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

SucursalesPagosSub.propTypes = { perfil: PropTypes.object }

const PrediccionesSub = ({ perfil }) => {
  const p = perfil.predicciones || {}
  const t = perfil.tendencia || {}

  // nivel_churn: bajo|medio|alto + riesgo_churn_pct numérico
  const churnRiesgo = Number(p.riesgo_churn_pct || 0)
  const nivel = String(p.nivel_churn || "").toLowerCase()
  const churnColor = nivel === "alto" ? "#dc2626" : nivel === "medio" ? "#f59e0b" : "#16a34a"
  const churnLabel = nivel ? nivel.charAt(0).toUpperCase() + nivel.slice(1) : (
    churnRiesgo >= 70 ? "Alto" : churnRiesgo >= 40 ? "Medio" : "Bajo"
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* WOW cards en grid 3x */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
        {/* Próxima compra */}
        <div style={{
          background: "linear-gradient(135deg, #dbeafe, #ffffff)",
          border: "1px solid #3b82f640", borderLeft: "4px solid #3b82f6",
          borderRadius: 14, padding: "18px 20px",
        }}>
          <div style={{ fontSize: 11, color: "#1e40af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            <i className="mdi mdi-calendar-clock me-1" />
            Próxima compra estimada
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 4 }}>
            {formatFechaLarga(p.proxima_compra_estimada)}
          </div>
          {p.dias_para_proxima_compra != null && (
            <div style={{ fontSize: 13, color: "#1e40af", marginTop: 4 }}>
              en <strong>{p.dias_para_proxima_compra}</strong> días
            </div>
          )}
          {p.probabilidad_compra_este_mes_pct != null && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: "#475569", marginBottom: 3 }}>
                Probabilidad este mes
              </div>
              <div style={{ height: 8, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
                <div style={{
                  width: `${Math.min(100, p.probabilidad_compra_este_mes_pct)}%`,
                  height: "100%", background: "#3b82f6",
                }} />
              </div>
              <div style={{ fontSize: 11, color: "#1e40af", marginTop: 3, fontWeight: 700, textAlign: "right" }}>
                {formatPercent(p.probabilidad_compra_este_mes_pct, 0)}
              </div>
            </div>
          )}
          {p.ticket_esperado_siguiente != null && (
            <div style={{ fontSize: 11, color: "#475569", marginTop: 10 }}>
              <i className="mdi mdi-cash me-1" />
              Ticket esperado: <strong>{formatCurrency(p.ticket_esperado_siguiente)}</strong>
            </div>
          )}
        </div>

        {/* Churn */}
        <div style={{
          background: `linear-gradient(135deg, ${churnColor}1a, #ffffff)`,
          border: `1px solid ${churnColor}40`, borderLeft: `4px solid ${churnColor}`,
          borderRadius: 14, padding: "18px 20px",
        }}>
          <div style={{ fontSize: 11, color: churnColor, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            <i className="mdi mdi-alert-decagram-outline me-1" />
            Riesgo de churn
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: churnColor, marginTop: 4, lineHeight: 1 }}>
            {formatPercent(churnRiesgo, 1)}
          </div>
          <div style={{ fontSize: 13, color: churnColor, marginTop: 4, fontWeight: 700 }}>
            Nivel: {churnLabel}
          </div>
          <div style={{ fontSize: 11, color: "#475569", marginTop: 8 }}>
            Probabilidad de que el cliente deje de comprar en los próximos meses.
          </div>
        </div>

        {/* LTV */}
        <div style={{
          background: "linear-gradient(135deg, #d1fae5, #ffffff)",
          border: "1px solid #10b98140", borderLeft: "4px solid #10b981",
          borderRadius: 14, padding: "18px 20px",
        }}>
          <div style={{ fontSize: 11, color: "#065f46", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            <i className="mdi mdi-cash-fast me-1" />
            LTV estimado
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 4 }}>
            {formatCurrency(p.ltv_estimado)}
          </div>
          <div style={{ fontSize: 13, color: "#065f46", marginTop: 4 }}>
            valor de vida proyectado
          </div>
          <div style={{ fontSize: 11, color: "#475569", marginTop: 8 }}>
            <i className="mdi mdi-information-outline me-1" />
            Proyección lineal basada en frecuencia y ticket promedio.
          </div>
        </div>
      </div>

      {/* Caja informativa de tendencia */}
      {t.direccion && (
        <div style={{
          background: "#eff6ff", border: "1px solid #bfdbfe",
          color: "#1e40af", padding: "12px 16px", borderRadius: 10,
          fontSize: 13, display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-information-outline" style={{ fontSize: 20 }} />
          <span>
            Las predicciones se basan en la frecuencia de compra histórica, regularidad y tendencia <strong>{t.direccion}</strong>.
          </span>
        </div>
      )}
    </div>
  )
}

PrediccionesSub.propTypes = { perfil: PropTypes.object }

const SugerenciasSub = ({ perfil }) => {
  const sug = perfil.sugerencias_mercadeo || perfil.sugerencias || []

  // Acción CTA: copiar el contenido al portapapeles + tooltip "próximamente CRM"
  const handleCopiar = async (s) => {
    const texto = [s.mensaje, s.razon, s.accion_sugerida].filter(Boolean).join("\n\n")
    try {
      await navigator.clipboard.writeText(texto)
    } catch {}
  }

  if (!sug.length) {
    return (
      <div style={{ background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 12, padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-lightbulb-off-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">No hay sugerencias activas para este cliente.</div>
      </div>
    )
  }

  // Ordenar por prioridad (alta → media → baja)
  const orden = { alta: 0, media: 1, baja: 2, ALTA: 0, MEDIA: 1, BAJA: 2 }
  const sorted = [...sug].sort((a, b) => (orden[a.prioridad] ?? 99) - (orden[b.prioridad] ?? 99))

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {sorted.map((s, i) => {
        const cfg = sugerenciaConfig(s.tipo)
        const pri = prioridadConfig(s.prioridad)
        return (
          <div key={i} style={{
            background: "#fff", border: "1px solid #e2e8f0",
            borderLeft: `4px solid ${cfg.color}`,
            borderRadius: 12, padding: "14px 18px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{
                  padding: "3px 10px", borderRadius: 999,
                  background: cfg.bg, color: cfg.color,
                  fontSize: 11, fontWeight: 700,
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}>
                  <i className={`mdi mdi-${cfg.icon}`} />
                  {cfg.label}
                </span>
                <span style={{
                  padding: "2px 8px", borderRadius: 999,
                  background: pri.bg, color: pri.color,
                  fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                }}>
                  Prioridad {pri.label}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleCopiar(s)}
                title="Copiar al portapapeles · próximamente conectado a CRM"
                className="btn btn-sm btn-outline-primary"
                style={{ fontSize: 11, padding: "3px 10px" }}
              >
                <i className="mdi mdi-content-copy me-1" />
                Copiar
              </button>
            </div>
            {s.mensaje && (
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginTop: 8 }}>
                {s.mensaje}
              </div>
            )}
            {s.razon && (
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, fontStyle: "italic" }}>
                <i className="mdi mdi-comment-question-outline me-1" />
                {s.razon}
              </div>
            )}
            {s.accion_sugerida && (
              <div style={{
                marginTop: 8, fontSize: 12, color: cfg.color, fontWeight: 600,
                background: cfg.bg, padding: "8px 12px", borderRadius: 6,
              }}>
                <i className="mdi mdi-arrow-right-bold me-1" />
                {s.accion_sugerida}
              </div>
            )}
            {s.datos && Object.keys(s.datos).length > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8" }}>
                {Object.entries(s.datos).map(([k, v]) => (
                  <span key={k} style={{ marginRight: 10 }}>
                    <strong>{k}:</strong> {String(v)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
      })}

      <div style={{
        background: "#f8fafc", border: "1px dashed #cbd5e1",
        color: "#64748b", padding: "10px 14px", borderRadius: 10,
        fontSize: 12, display: "flex", alignItems: "center", gap: 8,
      }}>
        <i className="mdi mdi-information-outline" />
        Las acciones &laquo;Copiar&raquo; pasan el texto al portapapeles. La integración directa con el CRM llegará en una próxima fase.
      </div>
    </div>
  )
}

SugerenciasSub.propTypes = { perfil: PropTypes.object }

// ───────── Drawer principal ─────────

const DrawerPerfil360 = ({ abierto, nitReceptor, clienteBase, filtros, onCerrar }) => {
  const [perfil, setPerfil] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [sub, setSub] = useState("resumen")

  const cargar = useCallback(async () => {
    if (!abierto || !nitReceptor || !filtros) return
    setLoading(true)
    setError("")
    setPerfil(null)
    try {
      const r = await getPerfilDetalle(nitReceptor, filtros)
      const body = r.data
      if (body?.success === false) throw new Error(body.error || body.message || "Error en el servidor")
      // El back envuelve el perfil en `data` (también acepta `perfil` o cruda por compat)
      setPerfil(body?.data || body?.perfil || body)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el perfil.")
    } finally {
      setLoading(false)
    }
  }, [abierto, nitReceptor, filtros])

  useEffect(() => {
    if (abierto) {
      setSub("resumen")
      cargar()
    }
  }, [abierto, cargar])

  // Bloquea scroll del body cuando el drawer está abierto
  useEffect(() => {
    if (abierto) {
      document.body.style.overflow = "hidden"
      return () => { document.body.style.overflow = "" }
    }
  }, [abierto])

  const headerInfo = useMemo(() => {
    const cfg = segmentoConfig(perfil?.rfm?.segmento || clienteBase?.rfm?.segmento)
    const nombre = perfil?.receptor?.nombre_canonico
      || perfil?.receptor?.nombre
      || clienteBase?.nombre
      || "Cliente"
    const nit = perfil?.receptor?.nit
      || clienteBase?.nit_receptor
      || nitReceptor
    return { cfg, nombre, nit }
  }, [perfil, clienteBase, nitReceptor])

  if (!abierto) return null

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCerrar}
        style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
          zIndex: 1040, backdropFilter: "blur(2px)",
        }}
      />
      {/* Drawer */}
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(1100px, 95vw)",
        background: "#f8fafc", zIndex: 1041,
        display: "flex", flexDirection: "column",
        boxShadow: "-12px 0 40px rgba(15,23,42,0.18)",
        animation: "slideInRight .25s ease",
      }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(40px); opacity: .6 } to { transform: translateX(0); opacity: 1 } }`}</style>

        {/* Header */}
        <div style={{
          background: "#fff", borderBottom: "1px solid #e2e8f0",
          padding: "14px 20px",
          display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: `${headerInfo.cfg.color}1a`, color: headerInfo.cfg.color,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 700, flexShrink: 0,
          }}>
            <span>{headerInfo.cfg.emoji}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Perfil 360°
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {headerInfo.nombre}
            </div>
            <div style={{ fontSize: 11, color: "#64748b" }}>
              NIT {headerInfo.nit}
              {(perfil?.rfm?.segmento || clienteBase?.rfm?.segmento) && (
                <span style={{
                  marginLeft: 8, padding: "1px 8px", borderRadius: 999,
                  background: headerInfo.cfg.bg, color: headerInfo.cfg.color,
                  fontWeight: 700,
                }}>
                  {headerInfo.cfg.label}
                </span>
              )}
            </div>
          </div>
          <button type="button" onClick={onCerrar} style={{
            background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
            color: "#475569", cursor: "pointer", padding: "6px 10px", fontSize: 13,
          }}>
            <i className="mdi mdi-close me-1" />Cerrar
          </button>
        </div>

        {/* Sub-tabs */}
        <div style={{
          background: "#fff", borderBottom: "1px solid #e2e8f0",
          padding: "0 14px", display: "flex", gap: 2, overflowX: "auto", flexShrink: 0,
        }}>
          {TABS.map(t => {
            const activo = sub === t.id
            return (
              <button
                key={t.id} type="button" onClick={() => setSub(t.id)}
                style={{
                  background: "none", border: "none", padding: "12px 14px",
                  fontSize: 12, fontWeight: 600,
                  color: activo ? "#0f172a" : "#64748b",
                  borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                  whiteSpace: "nowrap",
                }}
              >
                <i className={`mdi ${t.icon}`} />
                {t.label}
              </button>
            )
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {loading && (
            <div style={{ padding: 80, textAlign: "center", color: "#64748b" }}>
              <Spinner color="primary" />
              <div className="mt-2">Cargando perfil 360°…</div>
            </div>
          )}

          {error && !loading && (
            <div style={{
              padding: "12px 16px", background: "#fef2f2", color: "#991b1b",
              border: "1px solid #fecaca", borderRadius: 10,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <i className="mdi mdi-alert-circle" />
              <span style={{ flex: 1 }}>{error}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={cargar}>Reintentar</button>
            </div>
          )}

          {perfil && !loading && !error && (
            <>
              {sub === "resumen"        && <ResumenSub perfil={perfil} clienteBase={clienteBase} />}
              {sub === "tendencia"      && <TendenciaSub perfil={perfil} />}
              {sub === "comportamiento" && <ComportamientoSub perfil={perfil} />}
              {sub === "productos"      && <ProductosSub perfil={perfil} />}
              {sub === "sucursales"     && <SucursalesPagosSub perfil={perfil} />}
              {sub === "predicciones"   && <PrediccionesSub perfil={perfil} />}
              {sub === "sugerencias"    && <SugerenciasSub perfil={perfil} />}
            </>
          )}
        </div>
      </aside>
    </>
  )
}

DrawerPerfil360.propTypes = {
  abierto: PropTypes.bool,
  nitReceptor: PropTypes.string,
  clienteBase: PropTypes.object,
  filtros: PropTypes.object,
  onCerrar: PropTypes.func.isRequired,
}

export default DrawerPerfil360
